import { useCallback, useMemo } from 'react';
import type { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { globalErrorContexts } from '../../constants.tsx';
import type { InlineErrorsType, ValidationErrorContext } from '../../types.tsx';
import { getFirstValidationErrorMessage } from '../get-validation-error/index.tsx';
import { formatHelpError } from '../transform-help-error/index.tsx';
import { transformToInlineErrors } from '../transform-to-inline-errors/index.tsx';
import { messages } from './messages.tsx';

const rlabsInsightMessageLookup: Record<string, MessageDescriptor> = {
	'rlabs.insight.i18n.constraint.violation.ReferenceTypeBean.Size.name':
		messages.constraintViolationReferenceTypeBeanSizeName,
	'rlabs.insight.i18n.constraint.violation.StatusTypeBean.NotNull.name':
		messages.constraintViolationStatusTypeBeanNotNullName,
	'rlabs.insight.i18n.constraint.violation.StatusTypeBean.Size.name':
		messages.constraintViolationStatusTypeBeanSizeName,
	'rlabs.insight.i18n.constraint.violation.StatusTypeBean.Size.description':
		messages.constraintViolationStatusTypeBeanSizeDescription,
	'rlabs.insight.i18n.constraint.violation.StatusTypeBean.Used':
		messages.constraintViolationStatusTypeBeanUsed,
	'rlabs.insight.i18n.constraint.violation.ObjectSchemaBean.Size.name':
		messages.constraintViolationObjectSchemaBeanSizeName,
	'rlabs.insight.i18n.constraint.violation.ObjectTypeBean.Size.name':
		messages.constraintViolationObjectTypeBeanSizeName,
	'rlabs.insight.i18n.constraint.violation.ObjectTypeBean.Size.description':
		messages.constraintViolationObjectTypeBeanSizeDescription,
	'rlabs.insight.i18n.constraint.violation.ObjectSchemaBean.Size.description':
		messages.constraintViolationObjectSchemaBeanSizeDescription,
	'rlabs.insight.i18n.constraint.violation.VariablesEntry.Size.name':
		messages.constraintViolationVariablesEntrySizeName,
	'rlabs.insight.i18n.constraint.violation.VariablesEntry.Pattern.objectSchemaKey':
		messages.constraintViolationVariablesEntryPatternObjectSchemaKey,
	'rlabs.insight.i18n.constraint.violation.VariablesEntry.Size.description':
		messages.constraintViolationVariablesEntrySizeDescription,
	'rlabs.insight.i18n.import.validation.name.duplicate': messages.errorDuplicateName,
};

const i18nMessageLookup: Record<string, MessageDescriptor> = {
	'i18n.constraint.violation.ReferenceTypeBean.Size.name':
		messages.constraintViolationReferenceTypeBeanSizeName,
	'i18n.constraint.violation.StatusTypeBean.NotNull.name':
		messages.constraintViolationStatusTypeBeanNotNullName,
	'i18n.constraint.violation.StatusTypeBean.Size.name':
		messages.constraintViolationStatusTypeBeanSizeName,
	'i18n.constraint.violation.StatusTypeBean.Size.description':
		messages.constraintViolationStatusTypeBeanSizeDescription,
	'i18n.constraint.violation.StatusTypeBean.Used': messages.constraintViolationStatusTypeBeanUsed,
	'i18n.constraint.violation.ObjectSchemaBean.Size.name':
		messages.constraintViolationObjectSchemaBeanSizeName,
	'i18n.constraint.violation.ObjectTypeBean.Size.name':
		messages.constraintViolationObjectTypeBeanSizeName,
	'i18n.constraint.violation.ObjectTypeBean.Size.description':
		messages.constraintViolationObjectTypeBeanSizeDescription,
	'i18n.constraint.violation.ObjectSchemaBean.Size.description':
		messages.constraintViolationObjectSchemaBeanSizeDescription,
	'i18n.constraint.violation.VariablesEntry.Size.name':
		messages.constraintViolationVariablesEntrySizeName,
	'i18n.constraint.violation.VariablesEntry.Pattern.objectSchemaKey':
		messages.constraintViolationVariablesEntryPatternObjectSchemaKey,
	'i18n.constraint.violation.VariablesEntry.Size.description':
		messages.constraintViolationVariablesEntrySizeDescription,
	'i18n.import.validation.name.duplicate': messages.errorDuplicateName,
};

const messageLookup: Record<string, MessageDescriptor> = {
	...rlabsInsightMessageLookup,
	...i18nMessageLookup,
};

const useTranslateError = (): ((possiblyAnI18nKey: string) => string) => {
	const { formatMessage } = useIntl();

	return useCallback(
		(maybeAnI18nKey: string) => {
			const messageDescriptor = messageLookup[maybeAnI18nKey];
			if (messageDescriptor) {
				return formatMessage(messageDescriptor);
			}
			return maybeAnI18nKey;
		},
		[formatMessage],
	);
};

type ErrorTranslation = {
	transformToInlineErrors: (validationError: ValidationError) => InlineErrorsType;
	getFirstValidationErrorMessage: (validationError: ValidationError) => string | undefined;
	translateErrorString: (errorString: string) => string;
	transformToGlobalErrors: (
		validationError: ValidationError,
	) => { errorContext: ValidationErrorContext; errorMessage: string }[];
};
export const useCmdbErrorTranslation = (): ErrorTranslation => {
	const translateError = useTranslateError();

	return useMemo(
		() => ({
			transformToInlineErrors: (validationError: ValidationError) => {
				const inlineErrors = transformToInlineErrors(validationError);
				return Object.fromEntries(
					Object.entries(inlineErrors).map(([field, errorMessageMaybeAnI18nKey]) => [
						field,
						formatHelpError(translateError(errorMessageMaybeAnI18nKey)),
					]),
				);
			},
			getFirstValidationErrorMessage: (validationError: ValidationError) => {
				const firstError = getFirstValidationErrorMessage(validationError);
				return firstError ? formatHelpError(translateError(firstError)) : undefined;
			},
			translateErrorString: (errorString: string) => formatHelpError(translateError(errorString)),
			transformToGlobalErrors: (validationError: ValidationError) =>
				validationError.errors // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					.filter((e) => globalErrorContexts.includes(e.field as ValidationErrorContext))
					.map(({ field, error: errorMessage }) => ({
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						errorContext: field as ValidationErrorContext,
						errorMessage,
					})),
		}),
		[translateError],
	);
};
